import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppSidebar from '@/components/AppSidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Award, FileDown, RefreshCw, CheckCircle2, Recycle, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CertificationReportPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const [loading, setLoading] = useState(true);
  const [certs, setCerts] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/recycling/certificates`, config);
      setCerts(r.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const downloadCert = (c) => {
    const url = `${API}/recycling/loads/${c.id}/certificate/pdf?auth=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
    toast.success(`Certificate for ${c.manifest_no} opened`);
  };

  const certifiedCount = certs.filter((c) => c.certified).length;

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <AppSidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-black flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Award className="w-6 h-6 text-emerald-600" /> Certification Report
              </h1>
              <p className="text-sm text-gray-500">Completed end-of-life jobs · downloadable Certificate of Destruction &amp; Chain of Custody</p>
            </div>
            <Button variant="outline" size="sm" onClick={load} data-testid="certs-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {loading ? <div className="flex items-center justify-center h-64 text-gray-400">Loading certificates…</div> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <Card className="border-gray-200"><CardContent className="py-5 text-center">
                  <div className="text-3xl font-bold text-black" data-testid="certs-total">{certs.length}</div>
                  <div className="text-xs text-gray-500 mt-1">Completed Jobs</div></CardContent></Card>
                <Card className="border-gray-200"><CardContent className="py-5 text-center">
                  <div className="text-3xl font-bold text-emerald-600">{certifiedCount}</div>
                  <div className="text-xs text-gray-500 mt-1">Certified (closed)</div></CardContent></Card>
                <Card className="border-gray-200"><CardContent className="py-5 text-center">
                  <div className="text-3xl font-bold text-green-600">{certs.length - certifiedCount}</div>
                  <div className="text-xs text-gray-500 mt-1">Disposed (pending cert.)</div></CardContent></Card>
              </div>

              <h2 className="text-lg font-bold text-black mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Completed End-of-Life Jobs</h2>
              {certs.length === 0 ? (
                <div className="text-center text-gray-400 py-16 border border-dashed border-gray-200 rounded-xl" data-testid="no-certs">
                  <Recycle className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  No completed jobs yet. Loads appear here once they reach <b>Disposed</b> or <b>Certified</b>.
                </div>
              ) : (
                <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-3 px-4">Manifest</th><th className="px-4">Generator</th>
                      <th className="px-4">Waste Type</th><th className="px-4">Quantity</th>
                      <th className="px-4">Facility</th><th className="px-4">Status</th>
                      <th className="px-4">Completed</th><th className="px-4 text-right">Certificate</th>
                    </tr></thead>
                    <tbody>
                      {certs.map((c) => (
                        <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`cert-row-${c.id}`}>
                          <td className="py-3 px-4 font-medium text-black">#{c.manifest_no}</td>
                          <td className="px-4 text-gray-700">{c.generator_name}</td>
                          <td className="px-4 text-gray-700">{c.waste_type}</td>
                          <td className="px-4 text-gray-700">{c.quantity} {c.quantity_unit}</td>
                          <td className="px-4 text-gray-700">{c.facility_name}</td>
                          <td className="px-4">
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${c.certified ? 'bg-emerald-100 text-emerald-700' : 'bg-green-100 text-green-700'}`}>
                              <CheckCircle2 className="w-3 h-3" />{c.certified ? 'Certified' : 'Disposed'}
                            </span>
                          </td>
                          <td className="px-4 text-gray-500 text-xs">{c.completed_at ? new Date(c.completed_at).toLocaleDateString() : '—'}</td>
                          <td className="px-4 text-right">
                            <Button size="sm" variant="outline" onClick={() => downloadCert(c)} data-testid={`download-cert-${c.id}`}>
                              <FileDown className="w-4 h-4 mr-1" />PDF<ExternalLink className="w-3 h-3 ml-1" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
